export interface AgentInfo {
  slug: string;
  name: string;           // Human name
  title: string;          // Job title
  shortName: string;      // Display name (e.g., "CFO" or "Brand Guardian")
  department: string;     // Department key ("C-Suite", "00-Admin", etc.)
  departmentId: string;   // Numeric ID ("00", "01", etc.; empty for C-Suite)
  isCSuite: boolean;
  reportsTo: string;      // Slug of supervisor ("owner" for C-Suite)
  directReports: string[];
  skills: string[];
  agentDocPath: string;   // Relative path to agent .md file (from UT_ROOT)
}

export const AGENTS: AgentInfo[] = [
  // ── C-Suite ──────────────────────────────────────────────────────────
  {
    slug: 'ceo-agent', name: 'Elliot Vance', title: 'Chief Executive Officer', shortName: 'CEO',
    department: 'C-Suite', departmentId: '', isCSuite: true, reportsTo: 'owner',
    directReports: ['cfo-agent', 'cmo-agent', 'coo-agent', 'cco-agent'],
    skills: ['/ceo', '/strategy-articulation', '/coherence-audit', '/pre-mortem', '/cross-channel-synthesis', '/c-suite-report', '/c-suite-loop', '/kpi-snapshot', '/escalate', '/status-report', '/orchestration-dashboard'],
    agentDocPath: 'C-SUITE/06-Agents/CEO-Agent/CEO-Agent.md',
  },
  {
    slug: 'cfo-agent', name: 'Morgan Whitfield', title: 'Chief Financial Officer', shortName: 'CFO',
    department: 'C-Suite', departmentId: '', isCSuite: true, reportsTo: 'ceo-agent',
    directReports: ['sales-analyst-agent', 'multi-platform-analytics-agent', 'customer-intelligence-agent', 'research-discovery-agent', 'finance-tracker-agent'],
    skills: ['/cfo', '/c-suite-report', '/c-suite-loop', '/kpi-snapshot', '/escalate', '/status-report', '/orchestration-dashboard', '/finance-dashboard'],
    agentDocPath: 'C-SUITE/06-Agents/CFO-Agent/CFO-Agent.md',
  },
  {
    slug: 'cmo-agent', name: 'Sienna Brooks', title: 'Chief Marketing Officer', shortName: 'CMO',
    department: 'C-Suite', departmentId: '', isCSuite: true, reportsTo: 'ceo-agent',
    directReports: ['brand-guardian-agent', 'campaign-strategist-agent', 'lifecycle-marketing-agent', 'email-marketing-manager-agent', 'email-campaign-writer-agent', 'social-media-manager-agent', 'social-listening-agent', 'creative-strategist-agent', 'performance-marketing-agent', 'seo-web-strategist-agent', 'landing-page-strategist-agent', 'influencer-ugc-agent'],
    skills: ['/cmo', '/c-suite-report', '/c-suite-loop', '/kpi-snapshot', '/escalate', '/status-report', '/orchestration-dashboard'],
    agentDocPath: 'C-SUITE/06-Agents/CMO-Agent/CMO-Agent.md',
  },
  {
    slug: 'coo-agent', name: 'Marcus Chen', title: 'Chief Operations Officer', shortName: 'COO',
    department: 'C-Suite', departmentId: '', isCSuite: true, reportsTo: 'ceo-agent',
    directReports: ['base-management-agent', 'chat-bot-agent', 'chat-bot-orchestrator-agent', 'customer-experience-agent', 'executive-assistant-agent', 'legal-compliance-agent', 'product-catalog-agent', 'launch-coordinator-agent', 'marketplace-ops-agent', 'technical-ops-agent', 'secondbrain-agent'],
    skills: ['/coo', '/c-suite-report', '/c-suite-loop', '/kpi-snapshot', '/escalate', '/status-report', '/orchestration-dashboard'],
    agentDocPath: 'C-SUITE/06-Agents/COO-Agent/COO-Agent.md',
  },
  {
    slug: 'cco-agent', name: 'Paloma Reyes', title: 'Chief Creative Officer', shortName: 'CCO',
    department: 'C-Suite', departmentId: '', isCSuite: true, reportsTo: 'ceo-agent',
    directReports: ['slideshow-agent', 'image-gen-agent', 'canva-dam-agent', 'photoshoot-planning-agent', 'stylist-agent'],
    skills: ['/cco', '/c-suite-report', '/c-suite-loop', '/kpi-snapshot', '/escalate', '/status-report', '/orchestration-dashboard'],
    agentDocPath: 'C-SUITE/06-Agents/CCO-Agent/CCO-Agent.md',
  },

  // ── 00 Admin & Operations (COO) ─────────────────────────────────────
  {
    slug: 'base-management-agent', name: 'Archie Kovacs', title: 'Data Operations Specialist', shortName: 'Base Management',
    department: '00-Admin', departmentId: '00', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/airtable-table-builder', '/airtable-table-mapper', '/airtable-master-table-specialist'],
    agentDocPath: '00-ADMIN-and-OPERATIONS/06-Agents/Base-Management-Agent/Base Management Agent.md',
  },
  {
    slug: 'chat-bot-agent', name: 'Noor Patel', title: 'Customer Chat Specialist', shortName: 'Chat Bot',
    department: '00-Admin', departmentId: '00', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/copy-writing'],
    agentDocPath: '00-ADMIN-and-OPERATIONS/06-Agents/Chat-Bot-Agent/Chat Bot Agent.md',
  },
  {
    slug: 'chat-bot-orchestrator-agent', name: 'Felix Drummond', title: 'Conversation Router', shortName: 'Chat Orchestrator',
    department: '00-Admin', departmentId: '00', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: [],
    agentDocPath: '00-ADMIN-and-OPERATIONS/06-Agents/Chat-Bot-Orchestrator-Agent/Chat Bot Orchestrator Agent.md',
  },
  {
    slug: 'customer-experience-agent', name: 'Reina Okafor', title: 'Customer Experience Specialist', shortName: 'Customer Experience',
    department: '00-Admin', departmentId: '00', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/copy-writing', '/send-email'],
    agentDocPath: '00-ADMIN-and-OPERATIONS/06-Agents/Customer-Experience-Agent/Customer-Experience-Agent.md',
  },
  {
    slug: 'executive-assistant-agent', name: 'Solene Voss', title: 'Executive Assistant', shortName: 'Executive Assistant',
    department: '00-Admin', departmentId: '00', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/task-triage', '/deadline-check', '/task-followup', '/meeting-prep', '/orchestration-dashboard'],
    agentDocPath: '00-ADMIN-and-OPERATIONS/06-Agents/Executive-Assistant-Agent/Executive-Assistant-Agent.md',
  },
  {
    slug: 'legal-compliance-agent', name: 'Petra Langford', title: 'Legal & Compliance Monitor', shortName: 'Legal & Compliance',
    department: '00-Admin', departmentId: '00', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/compliance-check', '/license-tracker'],
    agentDocPath: '00-ADMIN-and-OPERATIONS/06-Agents/Legal-Compliance-Agent/Legal-Compliance-Agent.md',
  },

  // ── 01 Brand Foundation (CMO) ────────────────────────────────────────
  {
    slug: 'brand-guardian-agent', name: 'Celeste Moreau', title: 'Brand Guardian', shortName: 'Brand Guardian',
    department: '01-Brand', departmentId: '01', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/copy-writing'],
    agentDocPath: '01-BRAND-FOUNDATION/06-Agents/Brand-Guardian-Agent/Brand-Guardian-Agent.md',
  },

  // ── 02 Products & Apps (COO) ─────────────────────────────────────────
  {
    slug: 'product-catalog-agent', name: 'Dex Ramirez', title: 'Product Catalog Manager', shortName: 'Product Catalog',
    department: '02-Products', departmentId: '02', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: [],
    agentDocPath: '02-PRODUCTS-AND-APPS/06-Agents/Product-Catalog-Manager-Agent/Product Catalog Manager Agent.md',
  },
  {
    slug: 'launch-coordinator-agent', name: 'Taryn Wolfe', title: 'Product Launch Coordinator', shortName: 'Launch Coordinator',
    department: '02-Products', departmentId: '02', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/send-email'],
    agentDocPath: '02-PRODUCTS-AND-APPS/06-Agents/Product-Launch-Coordinator-Agent/Product Launch Coordinator Agent.md',
  },

  // ── 03 Campaigns (CMO) ──────────────────────────────────────────────
  {
    slug: 'campaign-strategist-agent', name: 'Juno Kessler', title: 'Campaign Strategist', shortName: 'Campaign Strategist',
    department: '03-Campaigns', departmentId: '03', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/location-freebie', '/import-ideas-to-content-scheduling'],
    agentDocPath: '03-CAMPAIGNS/06-Agents/Campaign-Strategist-Agent/Campaign-Strategist-Agent.md',
  },
  {
    slug: 'lifecycle-marketing-agent', name: 'Seren Calloway', title: 'Lifecycle Marketing Strategist', shortName: 'Lifecycle Marketing',
    department: '03-Campaigns', departmentId: '03', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: [],
    agentDocPath: '03-CAMPAIGNS/06-Agents/Lifecycle-Marketing-Agent/Lifecycle-Marketing-Agent.md',
  },

  // ── 04 Content Library (CCO) ─────────────────────────────────────────
  {
    slug: 'slideshow-agent', name: 'Kai Fontaine', title: 'Motion Content Creator', shortName: 'Slideshow',
    department: '04-Content', departmentId: '04', isCSuite: false, reportsTo: 'cco-agent',
    directReports: [], skills: ['/video-generate', '/video-thumbnails', '/copy-writing'],
    agentDocPath: '04-CONTENT-LIBRARY/06-Agents/Slideshow-Animations-Agent/Slideshow Animations Agent.md',
  },
  {
    slug: 'image-gen-agent', name: 'Sable Adeyemi', title: 'AI Art Director', shortName: 'Image Gen',
    department: '04-Content', departmentId: '04', isCSuite: false, reportsTo: 'cco-agent',
    directReports: [], skills: ['/image-generate', '/image-enhance'],
    agentDocPath: '04-CONTENT-LIBRARY/06-Agents/Image-Generation-Workflow-Agent/Image Generation Workflow Agent.md',
  },
  {
    slug: 'canva-dam-agent', name: 'Pip Odelberg', title: 'Digital Asset Manager', shortName: 'Canva DAM',
    department: '04-Content', departmentId: '04', isCSuite: false, reportsTo: 'cco-agent',
    directReports: [], skills: ['/canva-design-create', '/canva-to-airtable', '/canva-token-refresh', '/canva-asset-push'],
    agentDocPath: '04-CONTENT-LIBRARY/06-Agents/Canva-DAM-Sync-Agent/Canva DAM Sync Agent.md',
  },
  {
    slug: 'photoshoot-planning-agent', name: 'Lior Nazari', title: 'Production Coordinator', shortName: 'Photoshoot',
    department: '04-Content', departmentId: '04', isCSuite: false, reportsTo: 'cco-agent',
    directReports: [], skills: ['/shoot-setup'],
    agentDocPath: '04-CONTENT-LIBRARY/06-Agents/Photoshoot-Planning-Agent/Photoshoot Planning Agent.md',
  },
  {
    slug: 'stylist-agent', name: 'Vesper Tran', title: 'Stylist & Visual Merchandiser', shortName: 'Stylist',
    department: '04-Content', departmentId: '04', isCSuite: false, reportsTo: 'cco-agent',
    directReports: [], skills: ['/style-outfit', '/trend-report'],
    agentDocPath: '04-CONTENT-LIBRARY/06-Agents/Stylist-Agent/Stylist-Agent.md',
  },

  // ── 05 Email & SMS (CMO) ─────────────────────────────────────────────
  {
    slug: 'email-marketing-manager-agent', name: 'Lyra Sinclair', title: 'Email & SMS Marketing Manager', shortName: 'Email Marketing',
    department: '05-Email', departmentId: '05', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: [],
    agentDocPath: '05-EMAIL-and-SMS/06-Agents/Email-Marketing-Manager-Agent/Email-Marketing-Manager-Agent.md',
  },
  {
    slug: 'email-campaign-writer-agent', name: 'Wren Calloway', title: 'Email Campaign Writer', shortName: 'Email Writer',
    department: '05-Email', departmentId: '05', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/email-sequence', '/copy-writing', '/send-email'],
    agentDocPath: '05-EMAIL-and-SMS/06-Agents/Email-Campaign-Writer-Agent/Email Campaign Writer Agent.md',
  },

  // ── 06 Socials (CMO) ────────────────────────────────────────────────
  {
    slug: 'social-media-manager-agent', name: 'Zara Kimani', title: 'Social Media Manager', shortName: 'Social Media',
    department: '06-Socials', departmentId: '06', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/social-content', '/copy-writing', '/reddit-research'],
    agentDocPath: '06-SOCIALS/06-Agents/Social-Media-Manager-Agent/Social-Media-Manager-Agent.md',
  },
  {
    slug: 'social-listening-agent', name: 'Mika Sorrento', title: 'Social Listening & Engagement', shortName: 'Social Listening',
    department: '06-Socials', departmentId: '06', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/social-listen', '/draft-engagement', '/copy-writing', '/reddit-research'],
    agentDocPath: '06-SOCIALS/06-Agents/Social-Listening-Agent/Social-Listening-Agent.md',
  },

  // ── 07 Ads (CMO) ────────────────────────────────────────────────────
  {
    slug: 'creative-strategist-agent', name: 'Rio Vasquez', title: 'Creative Strategist', shortName: 'Creative Strategist',
    department: '07-Ads', departmentId: '07', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/competitor-ad-analysis', '/copy-writing', '/reddit-research'],
    agentDocPath: '07-ADS/06-Agents/Creative-Strategist-Agent/Creative-Strategist-Agent.md',
  },
  {
    slug: 'performance-marketing-agent', name: 'Maren Lindqvist', title: 'Performance Marketing Strategist', shortName: 'Performance Marketing',
    department: '07-Ads', departmentId: '07', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: [],
    agentDocPath: '07-ADS/06-Agents/Performance-Marketing-Agent/Performance-Marketing-Agent.md',
  },

  // ── 08 Website & SEO (CMO) ──────────────────────────────────────────
  {
    slug: 'seo-web-strategist-agent', name: 'Nico Hartwell', title: 'SEO & Web Strategist', shortName: 'SEO & Web',
    department: '08-Website', departmentId: '08', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/destination-blog', '/analytics-tracking', '/signup-flow-cro', '/copy-writing', '/reddit-research'],
    agentDocPath: '08-WEBSITE-and-SEO/06-Agents/SEO-Web-Strategist-Agent/SEO-Web-Strategist-Agent.md',
  },
  {
    slug: 'landing-page-strategist-agent', name: 'Clio Vance', title: 'Landing Page Strategist', shortName: 'Landing Pages',
    department: '08-Website', departmentId: '08', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/landing-page-create', '/broadcast-theme', '/copy-writing'],
    agentDocPath: '08-WEBSITE-and-SEO/06-Agents/Landing-Page-Strategist-Agent/Landing-Page-Strategist-Agent.md',
  },

  // ── 09 UGC & Influencers (CMO) ──────────────────────────────────────
  {
    slug: 'influencer-ugc-agent', name: 'Bria Sato', title: 'Influencer & UGC Manager', shortName: 'Influencer & UGC',
    department: '09-UGC', departmentId: '09', isCSuite: false, reportsTo: 'cmo-agent',
    directReports: [], skills: ['/copy-writing', '/send-email'],
    agentDocPath: '09-UGC-and-INFLUENCERS/06-Agents/Influencer-UGC-Manager-Agent/Influencer-UGC-Manager-Agent.md',
  },

  // ── 10 Marketplaces (COO) ───────────────────────────────────────────
  {
    slug: 'marketplace-ops-agent', name: 'Ezra Thorne', title: 'Marketplace Operations Manager', shortName: 'Marketplace Ops',
    department: '10-Marketplaces', departmentId: '10', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/sync-shopify-images'],
    agentDocPath: '10-MARKETPLACES/06-Agents/Marketplace-Operations-Agent/Marketplace-Operations-Agent.md',
  },

  // ── 11 Analytics (CFO) ──────────────────────────────────────────────
  {
    slug: 'sales-analyst-agent', name: 'Rowan Beckett', title: 'Sales Performance Analyst', shortName: 'Sales Analyst',
    department: '11-Analytics', departmentId: '11', isCSuite: false, reportsTo: 'cfo-agent',
    directReports: [], skills: ['/shopify-reports'],
    agentDocPath: '11-ANALYTICS/06-Agents/Sales-Performance-Analyst-Agent/Sales Performance Analyst Agent.md',
  },
  {
    slug: 'multi-platform-analytics-agent', name: 'Otto Brenner', title: 'Multi-Platform Analytics Specialist', shortName: 'Multi-Platform Analytics',
    department: '11-Analytics', departmentId: '11', isCSuite: false, reportsTo: 'cfo-agent',
    directReports: [], skills: ['/klaviyo-reports', '/meta-reports', '/etsy-reports', '/ga4-reports', '/microsoft-clarity-reports', '/pinterest-reports'],
    agentDocPath: '11-ANALYTICS/06-Agents/Multi-Platform-Analytics-Agent/Multi-Platform Analytics Agent.md',
  },
  {
    slug: 'customer-intelligence-agent', name: 'Iris Nakamura', title: 'Customer Intelligence Analyst', shortName: 'Customer Intelligence',
    department: '11-Analytics', departmentId: '11', isCSuite: false, reportsTo: 'cfo-agent',
    directReports: [], skills: [],
    agentDocPath: '11-ANALYTICS/06-Agents/Customer-Intelligence-Agent/Customer Intelligence Agent.md',
  },
  {
    slug: 'research-discovery-agent', name: 'Sage Ellington', title: 'Research & Discovery Specialist', shortName: 'Research & Discovery',
    department: '11-Analytics', departmentId: '11', isCSuite: false, reportsTo: 'cfo-agent',
    directReports: [], skills: ['/reddit-research'],
    agentDocPath: '11-ANALYTICS/06-Agents/Research-Discovery-Agent/Research & Discovery agent.md',
  },

  // ── 12 Finance (CFO) ────────────────────────────────────────────────
  {
    slug: 'finance-tracker-agent', name: 'Quinn Matsuda', title: 'Financial Tracker & Reconciliation', shortName: 'Finance Tracker',
    department: '12-Finance', departmentId: '12', isCSuite: false, reportsTo: 'cfo-agent',
    directReports: [], skills: [],
    agentDocPath: '12-FINANCE/06-Agents/Finance-Tracker-Agent/Finance Tracker Agent.md',
  },

  // ── 13 Codebases & Apps (COO) ───────────────────────────────────────
  {
    slug: 'technical-ops-agent', name: 'Dev Okonkwo', title: 'Technical Operations Lead', shortName: 'Technical Ops',
    department: '13-Codebases', departmentId: '13', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: ['/playwright', '/send-email'],
    agentDocPath: '13-CODEBASES-and-APPS/06-Agents/Technical-Operations-Agent/Technical-Operations-Agent.md',
  },
  {
    slug: 'secondbrain-agent', name: 'SecondBrain', title: 'AI Assistant Runtime', shortName: 'SecondBrain',
    department: '13-Codebases', departmentId: '13', isCSuite: false, reportsTo: 'coo-agent',
    directReports: [], skills: [],
    agentDocPath: '13-CODEBASES-and-APPS/UT-SecondBrain/workspace/AGENTS.md',
  },
];

export const DEPARTMENTS = [
  { id: '00', name: 'Admin & Operations' },
  { id: '01', name: 'Brand Foundation' },
  { id: '02', name: 'Products & Apps' },
  { id: '03', name: 'Campaigns' },
  { id: '04', name: 'Content Library' },
  { id: '05', name: 'Email & SMS' },
  { id: '06', name: 'Socials' },
  { id: '07', name: 'Ads' },
  { id: '08', name: 'Website & SEO' },
  { id: '09', name: 'UGC & Influencers' },
  { id: '10', name: 'Marketplaces' },
  { id: '11', name: 'Analytics' },
  { id: '12', name: 'Finance' },
  { id: '13', name: 'Codebases & Apps' },
];

const DEPT_COLORS: Record<string, string> = {
  'C-Suite': 'bg-accent-purple',
  '00-Admin': 'bg-avatar-blue',
  '01-Brand': 'bg-avatar-pink',
  '02-Products': 'bg-avatar-orange',
  '03-Campaigns': 'bg-avatar-yellow',
  '04-Content': 'bg-avatar-cyan',
  '05-Email': 'bg-avatar-green',
  '06-Socials': 'bg-avatar-pink',
  '07-Ads': 'bg-avatar-orange',
  '08-Website': 'bg-avatar-blue',
  '09-UGC': 'bg-avatar-yellow',
  '10-Marketplaces': 'bg-avatar-green',
  '11-Analytics': 'bg-avatar-cyan',
  '12-Finance': 'bg-avatar-orange',
  '13-Codebases': 'bg-avatar-blue',
};

// Who approves each agent's work (agent slug → approver slug)
export const APPROVAL_CHAINS: Record<string, string> = {
  // CEO → Owner
  'ceo-agent': 'owner',
  // C-Suite → CEO
  'cfo-agent': 'ceo-agent',
  'cmo-agent': 'ceo-agent',
  'coo-agent': 'ceo-agent',
  'cco-agent': 'ceo-agent',
  // CFO approves: 11-Analytics + 12-Finance
  'sales-analyst-agent': 'cfo-agent',
  'multi-platform-analytics-agent': 'cfo-agent',
  'customer-intelligence-agent': 'cfo-agent',
  'research-discovery-agent': 'cfo-agent',
  'finance-tracker-agent': 'cfo-agent',
  // CMO approves: 01, 03, 05, 06, 07, 08, 09
  'brand-guardian-agent': 'cmo-agent',
  'campaign-strategist-agent': 'cmo-agent',
  'lifecycle-marketing-agent': 'cmo-agent',
  'email-marketing-manager-agent': 'cmo-agent',
  'email-campaign-writer-agent': 'cmo-agent',
  'social-media-manager-agent': 'cmo-agent',
  'social-listening-agent': 'cmo-agent',
  'creative-strategist-agent': 'cmo-agent',
  'performance-marketing-agent': 'cmo-agent',
  'seo-web-strategist-agent': 'cmo-agent',
  'landing-page-strategist-agent': 'cmo-agent',
  'influencer-ugc-agent': 'cmo-agent',
  // COO approves: 00, 02, 10, 13
  'base-management-agent': 'coo-agent',
  'chat-bot-agent': 'coo-agent',
  'chat-bot-orchestrator-agent': 'coo-agent',
  'customer-experience-agent': 'coo-agent',
  'executive-assistant-agent': 'coo-agent',
  'legal-compliance-agent': 'coo-agent',
  'product-catalog-agent': 'coo-agent',
  'launch-coordinator-agent': 'coo-agent',
  'marketplace-ops-agent': 'coo-agent',
  'technical-ops-agent': 'coo-agent',
  'secondbrain-agent': 'coo-agent',
  // CCO approves: 04
  'slideshow-agent': 'cco-agent',
  'image-gen-agent': 'cco-agent',
  'canva-dam-agent': 'cco-agent',
  'photoshoot-planning-agent': 'cco-agent',
  'stylist-agent': 'cco-agent',
};

export function getAgentColor(department: string): string {
  return DEPT_COLORS[department] || 'bg-text-muted';
}

export function getAgentBySlug(slug: string): AgentInfo | undefined {
  return AGENTS.find(a => a.slug === slug);
}

export function getApprover(agentSlug: string): AgentInfo | undefined {
  const approverSlug = APPROVAL_CHAINS[agentSlug];
  if (!approverSlug || approverSlug === 'owner') return undefined;
  return AGENTS.find(a => a.slug === approverSlug);
}

export function getDirectReports(agentSlug: string): AgentInfo[] {
  return AGENTS.filter(a => a.reportsTo === agentSlug);
}

export function getAgentsByDepartment(deptKey: string): AgentInfo[] {
  return AGENTS.filter(a => a.department === deptKey);
}

export function getAgentsByCSuite(csuiteSlug: string): AgentInfo[] {
  return AGENTS.filter(a => a.reportsTo === csuiteSlug);
}
